<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
      <v-btn
        v-if="!isNew && !isDefault"
        class="mr-2"
        color="error"
        @click="deleteConfirmation = true"
      >
        <v-icon>mdi-delete</v-icon>
        <span class="d-none d-lg-block">{{ $t('button.delete') }}</span>
      </v-btn>
    </template>
    <template #content>
      <!-- Template Editor Dialog -->
      <message-center-template-dialog
        v-if="templateEditDialog"
        :visible="templateEditDialog"
        :template="template"
        :rule="r"
        @update-template="updateTemplate"
        @close="templateEditDialog = false"
      />
      <!--  -->
      <confirmation-dialog
        :visible="deleteConfirmation"
        :message="$t('message.confirm_delete_message_rule')"
        @confirm="remove"
        @close="deleteConfirmation = false"
      />

      <validation-observer ref="observer">
        <settings-group
          :title="$t('settings.messagecenter.rule_editor.rule_settings')"
          :subtitle="$t('settings.messagecenter.rule_editor.rule_settings_subtitle')"
        >
          <v-col
            class="py-0"
            cols="12"
            md="4"
          >
            <v-select
              v-model="r.messageType"
              :disabled="!isNew"
              :items="messageTypes"
              return-object
              :label="$t('title.message_type')"
            />
          </v-col>
          <!-- Event Type -->
          <v-col
            :disabled="isDefault || !isNew"
            class="py-0"
            cols="12"
            md="8"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                v-model="r.eventType"
                :disabled="isDefault || !isNew"
                :items="eventTypes"
                return-object
                :label="$t('label.event_type')"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <!-- Active -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-switch
              v-model="r.isInactive"
              :false-value="true"
              :true-value="false"
              hide-details
              :label="$t('label.active')"
            />
            <p class="pt-0 mt-0 ml-12">
              {{ $t('label.message_center_disable_rule') }}
            </p>
          </v-col>
          <!-- Send BCC -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-switch
              v-if="sendBccAllowed"
              v-model="r.sendBcc"
              class="pt-0 mt-0"
              hide-details
              :label="$t('label.send_bcc')"
            />
            <p
              v-if="sendBccAllowed"
              class="pt-0 mt-0 ml-12"
            >
              {{ $t('label.message_center_bcc_rule') }}
            </p>
          </v-col>
        </settings-group>

        <settings-group
          v-if="r.eventType && r.eventType.isTimed && !isNew"
          :title="$t('settings.messagecenter.rule_editor.rule_schedule')"
          :subtitle="$t('settings.messagecenter.rule_editor.rule_schedule_subtitle')"
        >
          <!-- Send days in advance -->
          <v-col
            v-if="r.eventType.isTimedBefore"
            cols="6"
            md="3"
            class="pb-0 pt-0"
          >
            <v-select
              v-model="r.sendDaysInAdvance"
              :items="daysAdvance"
              :label="$t('label.send_days_in_advance')"
            />
          </v-col>
          <!-- Send Days After -->
          <v-col
            v-if="r.eventType.isTimedAfter"
            cols="6"
            md="3"
            class="pb-0 pt-0"
          >
            <v-select
              v-model="r.sendDaysAfter"
              :items="daysAfter"
              :label="$t('label.send_days_after')"
            />
          </v-col>
          <!-- Time Picker -->
          <v-col
            cols="6"
            md="3"
            class="pb-0 pt-0"
          >
            <time-picker-field
              v-if="r.eventType && (r.eventType.isTimedAfter || r.eventType.isTimedBefore)"
              v-model="r.sendAtSpecificTime"
              :label="$t('label.send_at_time')"
            />
          </v-col>
        </settings-group>

        <settings-group
          v-if="!r.isDefault"
          gutter
          :title="$t('settings.messagecenter.rule_editor.advanced_filtering')"
          :subtitle="$t('settings.messagecenter.rule_editor.advanced_filtering_subtitle')"
        >
          <!-- Campaign -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-select
              v-model="r.campaign"
              :items="campaigns"
              return-object
              :label="$t('label.campaign')"
              @change="updateValue"
            />
          </v-col>
          <!-- Service -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-select
              v-model="r.service"
              :items="services"
              return-object
              :label="$t('label.service')"
              @change="updateValue"
            />
          </v-col>
          <!-- Section -->
          <v-col
            cols="12"
            class="pb-0 pt-0"
          >
            <v-select
              v-model="r.tab"
              :items="sections"
              return-object
              :label="$t('label.section')"
              @change="updateValue"
            />
          </v-col>
        </settings-group>

        <settings-group
          v-if="!isNew"
          gutter
          :title="$i18n.tc('label.custom_templates')"
          :subtitle="$t('settings.messagecenter.rule_editor.custom_templates_subtitle')"
        >
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-data-table
              class="clickable-table"
              :headers="headers"
              :items="templates"
              disable-pagination
              hide-default-header
              hide-default-footer
              mobile-breakpoint="0"
              :item-class="()=>'text-overline'"
              :no-data-text="$t('label.no_custom_templates')"
              @click:row="editTemplate"
            >
              <template #item.actions="props">
                <v-btn
                  icon
                  @click.stop="removeTemplate(props.item.id)"
                >
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
          <v-col
            v-if="!isNew"
            class="pt-0 d-flex justify-center"
            cols="12"
          >
            <v-btn
              outlined
              color="primary"
              @click="addTemplate"
            >
              <v-icon>
                mdi-plus
              </v-icon>
              {{ $t('button.add_custom_template') }}
            </v-btn>
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import UnsavedGuard from '@/mixins/unsaved-guard';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import MessageCenterTemplateDialog from '@/components/views/settings/MessageCenterTemplateDialog.vue';
import { strcmp } from '@/services/common';
import { performAction } from '@/services/vue-utils';
import MessageRule from '@/model/MessageRule';
import { cloneModel, isModelEqualToModel, uniqueMinId } from '@/model/model-utils';
import Campaign from '@/model/Campaign';
import Service from '@/model/Service';
import Tab from '@/model/Tab';
import MessageEventType from '@/model/MessageEventType';
import MessageTemplate from '@/model/MessageTemplate';
import Locale from '@/model/Locale';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import MessageCenterStore from '@/store/message-center-store';
import MessageType, { MessageTypeChannel } from '@/model/MessageType';

export default Vue.extend({
  name: 'MessageCenterRuleEditor',
  components: { ConfirmationDialog, SettingsForm, MessageCenterTemplateDialog },
  mixins: [UnsavedGuard, SettingsBreadcrumbs],

  data() {
    return {
      deleteConfirmation: false,
      r: new MessageRule(),
      oldr: new MessageRule(),
      template: new MessageTemplate(),
      breadcrumbs: [] as any,
      daysAdvance: [...new Array(31)].map(
        (e, i) => (
          { value: i, text: i === 0 ? this.$i18n.t('label.same_day') as string : this.$i18n.tc('label.x_days', i) }),
      ),
      daysAfter: [...new Array(31)].map(
        (e, i) => {
          let text = '';
          if (i === 0) {
            text = this.$i18n.t('label.same_day') as string;
          } else if (i === 1) {
            text = this.$i18n.t('label.next_day') as string;
          } else {
            text = this.$i18n.tc('label.x_days', i);
          }
          return { value: i, text };
        },
      ),
      headers: [
        {
          text: this.$i18n.t('label.custom_templates'),
          value: 'language',
          align: 'start',
          sortable: false,
        },
        {
          text: '',
          value: 'actions',
          align: 'right',
          sortable: false,
        },
      ],
      templateEditDialog: false,
    };
  },
  computed: {
    isChanged(): boolean {
      return !isModelEqualToModel(this.r, this.oldr);
    },
    isNew(): boolean {
      return this.r.id === 0;
    },
    isDefault(): boolean {
      return this.r.isDefault === true;
    },
    messageTypes(): MessageType[] {
      return this.$tstore.getters.messageCenterMessageTypes;
    },
    campaigns(): ({text: string, value: null} | { divider?: boolean} | Campaign)[] {
      return [
        { text: this.$i18n.tc('label.all_campaigns'), value: null },
        { divider: true },
        ...this.$tstore.state.messageCenter.campaigns ?? [],
      ];
    },
    services(): ({text: string, value: null} | { divider?: boolean} | Service)[] {
      return [
        { text: this.$i18n.tc('label.all_services'), value: null },
        { divider: true },
        ...this.$tstore.state.settings.services ?? [],
      ];
    },
    sections(): ({text: string, value: null} | { divider?: boolean} | Tab)[] {
      return [
        { text: this.$i18n.tc('label.all_sections'), value: null },
        { divider: true },
        ...this.$tstore.state.settings.tabs ?? [],
      ];
    },
    eventTypes(): MessageEventType[] {
      return this.$tstore.getters.messageCenterEventTypes;
    },
    templates(): {id: number, language: string}[] {
      const localesByLocale = this.$tstore.getters.localesByLocale as Map<string, Locale>;
      const customTemplates = this.r.templates.filter((o) => !o.isDefault && !o.isDeleted);
      const languages = customTemplates.map((o) => {
        const locale = o.locale ?? '';
        const language = localesByLocale.get(locale)?.language ?? locale;
        return { id: o.id, language };
      });
      const sortedLanguages = languages.sort(
        (o1: {language: string }, o2: {language: string }) => strcmp(o1.language, o2.language),
      );
      return sortedLanguages;
    },
    sendBccAllowed(): boolean {
      return this.r.messageType?.channel === MessageTypeChannel.EMAIL || false;
    },
  },
  watch: {
    r: {
      deep: true,
      handler(value: boolean, oldValue: boolean) {
        console.log('Rule changed', value, oldValue);
      },
    },
  },
  created() {
    loadStoreModule(this, 'messageCenter', MessageCenterStore, async () => { this.load(); });
  },
  beforeDestroy() {
    unloadStoreModule(this, 'messageCenter', async () => { this.$tstore.dispatch('resetMessageCenter'); });
  },
  mounted() {
    this.reset();
    this.breadcrumbs[this.breadcrumbs.length - 1].text = this.r.eventType;
  },
  methods: {
    async load() {
      if (this.$tstore.getters.isMessageCenterLoaded) return;
      const ok = await performAction(
        null,
        this.$i18n.tc('error.load_message_center'),
        async () => this.$tstore.dispatch('loadMessageCenter'),
      );
      if (ok) this.reset();
    },
    async save() {
      const ok = await performAction(
        this.$tc('message.success_changes_saved'),
        this.$i18n.tc('error.saving_changes'),
        async () => this.$tstore.dispatch('sendMessageRule', { entity: this.r }),
      );

      if (ok) {
        this.reset();
        this.$router.back();
      }
    },
    async remove() {
      const ok = await performAction(
        this.$tc('message.success_message_rule_deleted'),
        this.$tc('error.deleting_message_rule'),
        async () => this.$tstore.dispatch('sendMessageRule', { entity: this.r, delete: true }),
      );

      if (ok) {
        this.reset();
        this.$router.back();
      }
    },
    async reset() {
      this.templateEditDialog = false;

      this.breadcrumbs[this.breadcrumbs.length - 1].text = this.isNew
        ? this.$i18n.t('title.new_message_rule')
        : this.$i18n.t('title.message_rule');

      const id = Number(this.$route.params.id) || 0;
      const rules = (id < 0
        ? this.$tstore.getters.messageCenterBuiltinRules
        : this.$tstore.state.messageCenter.rules) ?? [];
      const r = rules.find((o: MessageRule) => o.id === id) ?? new MessageRule();
      this.r = cloneModel(r);
      this.oldr = r;
    },
    editTemplate(item: {id: number}) {
      this.templateEditDialog = true;
      this.template = this.r.templates.find((o) => o.id === item.id) ?? new MessageTemplate();
    },
    addTemplate() {
      this.templateEditDialog = true;
      this.template = new MessageTemplate();
      this.template.id = uniqueMinId(this.r.templates);
    },
    removeTemplate(id: number) {
      const index = this.r.templates.findIndex((o) => o.id === id);
      if (index < 0) return;

      if (this.oldr.templates.some((o) => o.id === id)) {
        this.r.templates[index].isDeleted = true;
      } else {
        this.r.templates.splice(index, 1);
      }
    },
    updateTemplate(t: MessageTemplate) {
      const isNewTemplate = this.template.id <= 0;
      if (isNewTemplate && !this.r.templates.some((o) => o.id === t.id)) {
        this.r.templates.push(t);
        this.r.templates.sort((o1, o2) => strcmp(o1.locale!, o2.locale!));
      } else {
        this.template.body = t.body;
        this.template.subject = t.subject;
      }
      this.templateEditDialog = false;
    },
    updateValue(value: string) {
      if (this.r.campaign?.value === null) this.r.campaign = null;
      if (this.r.service?.value === null) this.r.service = null;
      if (this.r.tab?.value === null) this.r.tab = null;
    },
  },
});
</script>

<style lang="css">

.custom-template-row {
  border-bottom: 1px red solid !important;
}

</style>
